import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import usePrice from '@core/composables/use-price';
import i18n from '@monolith/legacy/services/i18n';
import { getTieredDiscountsForRetailer } from '../services/api';
import { type TieredDiscount } from '../types';
import RootStoreHelpers from '@monolith/legacy/store/helpers';
import { Amount } from '@core/types/amount';
import type { AnkorstoreInternalTierResource } from '@ankorstore/api-internal/data-contracts';
import type { IconName } from '@ankorstore/design-system';

// Module-scope cache that resets when the page changes
const cachedTieredDiscounts = ref<TieredDiscount[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
let ongoingFetchPromise: Promise<TieredDiscount[]> | null = null;

// Module-scope flag to track if hook is already added
let isRouterHookAdded = false;

export function useTieredDiscounts() {
  const router = useRouter();
  const { userIsRetailer } = RootStoreHelpers.useGetters(['userIsRetailer']);

  // Clear cache when the page changes (only once per app lifetime)
  if (!isRouterHookAdded) {
    router.afterEach((to, from) => {
      // A query-only change keeps components mounted and reading this cache.
      if (to.path === from.path) {
        return;
      }

      cachedTieredDiscounts.value = [];
      error.value = null;
      ongoingFetchPromise = null;
    });
    isRouterHookAdded = true;
  }

  // Lazy load if needed
  const ensureLoaded = async (): Promise<TieredDiscount[]> => {
    // If the user is not a retailer, clear and return empty data
    if (!userIsRetailer.value) {
      cachedTieredDiscounts.value = [];
      return cachedTieredDiscounts.value;
    }

    if (cachedTieredDiscounts.value.length > 0) {
      return cachedTieredDiscounts.value;
    }

    if (ongoingFetchPromise === null) {
      isLoading.value = true;
      error.value = null;

      ongoingFetchPromise = getTieredDiscountsForRetailer()
        .then((response) => {
          // Handle null response (API error already captured in api.ts) or valid response with data array
          const data = response?.data ?? [];
          cachedTieredDiscounts.value = data;
          return data;
        })
        .catch((err) => {
          error.value = err.message || 'Failed to load tiered discounts';
          ongoingFetchPromise = null; // Allow retry
          throw err;
        })
        .finally(() => {
          isLoading.value = false;
        });
    }

    return ongoingFetchPromise;
  };

  const isLoaded = computed(() => cachedTieredDiscounts.value.length > 0);

  // Business logic methods
  const hasTieredDiscount = (brandUuid: string) => {
    return cachedTieredDiscounts.value.some((discount) => discount.attributes.brandUuid === brandUuid);
  };

  const getTieredDiscountTier = (brandUuid: string): TieredDiscount['attributes'] | undefined => {
    return cachedTieredDiscounts.value.find((discount) => discount.attributes.brandUuid === brandUuid)?.attributes;
  };

  const hasVolumeBasedDiscount = (brandUuid: string): boolean => {
    const attributes = getTieredDiscountTier(brandUuid);
    if (!attributes) {
      return false;
    }

    // Multi-tier structure - check if any tier has a minimum order value
    return attributes.tiers.some((tier) => tier.minimumOrderValue && tier.minimumOrderValue.amount > 0);
  };

  // Get the maximum discount percentage
  const getMaxDiscountPercentage = (brandUuid: string): number => {
    const attributes = getTieredDiscountTier(brandUuid);
    if (!attributes) {
      return 0;
    }

    // Multi-tier structure - return maxPercentage
    return attributes.maxPercentage;
  };

  // Get all discount tiers
  const getDiscountTiers = (brandUuid: string): AnkorstoreInternalTierResource[] => {
    const attributes = getTieredDiscountTier(brandUuid);
    if (!attributes) {
      return [];
    }

    // Multi-tier structure - return tiers array
    return attributes.tiers;
  };

  const getVolumeBasedDiscountTier = (brandUuid: string): AnkorstoreInternalTierResource | undefined => {
    const attributes = getTieredDiscountTier(brandUuid);
    if (!attributes) {
      return undefined;
    }

    // For multi-tier, check if any tier has a minimum order value
    if (hasVolumeBasedDiscount(brandUuid)) {
      // Find and return the tier with the highest percentage (maxPercentage)
      // This tier represents the maximum discount available
      const highestTier = attributes.tiers.find((tier) => tier.percentage === attributes.maxPercentage);
      return highestTier;
    }
    return undefined;
  };

  // Get discount graph data for visualization
  const getDiscountGraphData = (tiers: AnkorstoreInternalTierResource[]) => {
    if (!tiers || tiers.length === 0) {
      return null;
    }

    const { formatPrice } = usePrice();

    // Create a copy of tiers to avoid mutating the original
    const graphTiers = [...tiers];

    const currency = graphTiers[graphTiers.length - 1].minimumOrderValue?.currency || 'EUR';

    // If the first tier doesn't start at 0, add a starting point with 0 discount
    if (graphTiers[0].minimumOrderValue?.amount > 0) {
      // Add a zero tier at the beginning
      graphTiers.unshift({
        percentage: 0,
        minimumOrderValue: {
          amount: 0,
          currency,
        },
      });
    }

    return {
      dots: graphTiers.map((tier) =>
        formatPrice((tier.minimumOrderValue as Amount) || ({ amount: 0, currency } as Amount), '$0')
      ),
      areas: graphTiers.map((tier) => `${tier.percentage}%`),
      dotsLabel: i18n.global.t('brands.network.discountsDrawer.discountGraph.dotsLabel'),
      areasLabel: i18n.global.t('brands.network.discountsDrawer.discountGraph.areasLabel'),
      title: i18n.global.t('cart.tieredDiscount.badge.label'),
      titleIcon: 'percentage-offer' as IconName,
    };
  };

  return {
    tieredDiscounts: cachedTieredDiscounts,
    isLoading,
    error,
    isLoaded,

    // Actions
    ensureLoaded,

    // Business logic computed methods
    hasTieredDiscount,
    getTieredDiscountTier,
    hasVolumeBasedDiscount,
    getVolumeBasedDiscountTier,

    // Multi-tier methods
    getMaxDiscountPercentage,
    getDiscountTiers,
    getDiscountGraphData,
  };
}
